import _ from 'underscore';
import React, { useContext, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import {
  Text,
  Button,
  mergeClasses,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Label,
} from '@fluentui/react-components';
import { SearchableSelectorWithDefaultList } from '@bingads-webui-campaign-react/searchable-selector-with-default-list';
import { typeaheadCompanyPageSearch } from '@bingads-webui-campaign-react/linkedin-data';
import { LinkedInLogo } from '@bingads-webui-campaign-react/linkedin-shared-components';

import { ComponyPageItem, TermsAndConditions } from '../shared-components';
import { COMPANY_PAGE_LINKING_ACTIONS } from '../reducer';
import { useSaveCompanyPage } from '../hooks';
import { useCompanyPageSelectionStageStyles } from '../styles/company-page-selection-stage-styles';

export const CompanyPageSelectionStage = (props) => {
  const {
    state, dispatch, onCancel, onContinue, serverError, logger,
  } = props;

  const classes = useCompanyPageSelectionStageStyles();
  const i18n = useContext(I18nContext);

  const serverContext = useContext(ServerContext);
  const aid = useMemo(() => serverContext.CurrentAccount.Id, [serverContext]);
  const cid = useMemo(() => serverContext.CurrentCustomer.Id, [serverContext]);

  useSaveCompanyPage(state, dispatch, onContinue, logger);

  const [termsAndConditionsAccepted, setTermsAndConditionsAccepted] = useState(false);

  const { companyPages, selectedCompanyPage = {} } = state;
  const {
    Id: selectedCompanyPageId = null,
    // Name: selectedCompanyPageName,
  } = selectedCompanyPage || {};

  const selectedOptions = useMemo(() => (selectedCompanyPageId ? [selectedCompanyPageId] : []), [selectedCompanyPageId]);

  const disableAllowButton = !selectedCompanyPageId || !termsAndConditionsAccepted;

  const onChangePageSelection = useCallback((newSelectedCompanyPage) => {
    logger.trace(`Selected company page: ${JSON.stringify(newSelectedCompanyPage)}`);
    if (newSelectedCompanyPage) {
      const isAdminOf = companyPages.some(({ Id }) => Id === newSelectedCompanyPage.Id);
      dispatch({
        type: COMPANY_PAGE_LINKING_ACTIONS.UPDATE_SELECTED_COMPANY_PAGE,
        selectedCompanyPage: { ...newSelectedCompanyPage, isAdminOf },
      });
    } else {
      dispatch({
        type: COMPANY_PAGE_LINKING_ACTIONS.UPDATE_SELECTED_COMPANY_PAGE,
        selectedCompanyPage: null,
      });
    }
  }, [dispatch, logger, companyPages]);

  const onContinueClicked = useCallback(() => {
    if (selectedCompanyPage.isAdminOf) {
      dispatch({ type: COMPANY_PAGE_LINKING_ACTIONS.START_SAVING_COMPANY_PAGE });
    } else {
      dispatch({ type: COMPANY_PAGE_LINKING_ACTIONS.CONFIRM_LINK_COMPANY_PAGE });
    }
  }, [dispatch, selectedCompanyPage]);

  const fetchCompanies = useCallback(
    (query) => {
      if (!query) return Promise.resolve([]);

      return typeaheadCompanyPageSearch(cid, aid, query).then(({ CompanyPages }) => CompanyPages);
    }
    , [cid, aid]
  );

  const customRender = useCallback(({
    Name, LogoDownloadUrl, Location, IndustryType,
  }) => (
    <ComponyPageItem
      companyPageName={Name}
      companyPageLogoSrc={LogoDownloadUrl}
      industryLocation={Location}
      industryType={IndustryType}
    />
  ), []);

  return (
    <div className={mergeClasses('company-page-selection-stage', classes.container)}>
      <div className={classes.contentContainer}>
        <LinkedInLogo />
        <div className={mergeClasses('connect-company-page-description', classes.descriptionContainer)}>
          <Text as="h1" className={classes.title}>
            {i18n.getString(_TL_('Connect to your company’s LinkedIn page'))}
          </Text>
          <Text className={classes.itemText}>{i18n.getString(_TL_('Connect to your company to advertise on LinkedIn.'))}</Text>
        </div>
        <div className={mergeClasses('connect-to-company-page', classes.companyPageContainer)}>
          {selectedCompanyPageId ? (
            <div>
              <Label>{i18n.getString(_TL_('Connect'))}</Label>
              <div className={mergeClasses('selected-company-page', classes.selectedCompanyPageContainer)}>
                <div className={mergeClasses('company-page-info', classes.companyPageInfo)}>
                  <ComponyPageItem
                    companyPageName={selectedCompanyPage.Name}
                    companyPageLogoSrc={selectedCompanyPage.LogoDownloadUrl}
                    industryLocation={selectedCompanyPage.Location}
                    industryType={selectedCompanyPage.IndustryType}
                  />
                </div>
                <div className={classes.companyPageActions}>
                  <Button
                    className="choose-again-button"
                    appearance="transparent"
                    onClick={() => onChangePageSelection(null)}
                  >
                    {i18n.getString(_TL_('Update'))}
                  </Button>
                </div>
              </div>
            </div>
      ) : (
        <SearchableSelectorWithDefaultList
          className={mergeClasses('company-page-selector', classes.selector)}
          label={i18n.getString(_TL_('Connect to your company’s page'))}
          loadingText={i18n.getString(_TL_('Loading companies...'))}
          placeholderText={i18n.getString(_TL_('Select your company page'))}
          noResultsText={i18n.getString(_TL_('No companies found'))}
          fetchFn={fetchCompanies}
          onRenderOption={customRender}
          value={selectedOptions}
          onSelectOption={onChangePageSelection}
          defaultGroupName={i18n.getString(_TL_('Pages that you are admin of'))}
          defaultResults={companyPages}
          searchResultGroupName={i18n.getString(_TL_('Company page'))}
        />
      )}
        </div>
        <div className="terms-and-conditions">
          <TermsAndConditions
            checked={termsAndConditionsAccepted}
            onChange={setTermsAndConditionsAccepted}
          />
        </div>
      </div>
      {serverError}
      <div className={classes.actionsContainer}>
        <Button className="step-cancel-button" onClick={onCancel}>
          {i18n.getString(_TL_('Cancel'))}
        </Button>

        <Dialog modalType="non-modal">
          <DialogTrigger disableButtonEnhancement>
            <Button
              appearance="primary"
              className="allow-button"
              disabled={disableAllowButton}
            >
              {i18n.getString(_TL_('Next'))}
            </Button>
          </DialogTrigger>
          <DialogSurface>
            <DialogBody className="confirm-dialog">
              <DialogTitle>{i18n.getString(_TL_('You cannot change your company page once you’ve connected'))}</DialogTitle>
              <DialogContent>{i18n.getString(_TL_('After connecting this account to this LinkedIn Company Page, you will not be able to change the connection for this account.'))}
              </DialogContent>
              <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                  <Button
                    appearance="primary"
                    className="continue-button"
                    onClick={onContinueClicked}
                  >{i18n.getString(_TL_('Continue'))}
                  </Button>
                </DialogTrigger>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>

      </div>
    </div>
  );
};

CompanyPageSelectionStage.propTypes = {
  state: PropTypes.shape({
    companyPages: PropTypes.arrayOf(PropTypes.shape({})),
    selectedCompanyPage: PropTypes.shape({
      Id: PropTypes.string,
      Name: PropTypes.string,
    }),
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  serverError: PropTypes.node,
  logger: PropTypes.shape({
    trace: PropTypes.func,
    error: PropTypes.func,
  }),
};

CompanyPageSelectionStage.defaultProps = {
  serverError: null,
  logger: {
    trace: _.noop,
    error: _.noop,
  },
};
